import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, toCents } from "@/lib/format";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const BRANDS = ["bb", "betos"];
const UNITS = ["trabajo", "hora", "pie_cuadrado"];

const RateRow = ({ brand, propertyId, st, rate, canEdit, onSaved }) => {
  const { t } = useT();
  const [precio, setPrecio] = useState(rate ? (rate.precio_cliente_cents / 100).toFixed(2) : "");
  const [costo, setCosto] = useState(rate ? (rate.costo_sub_cents / 100).toFixed(2) : "");
  const [unidad, setUnidad] = useState(rate?.unidad || "trabajo");
  const [dirty, setDirty] = useState(false);

  const save = useMutation({
    mutationFn: async () =>
      (await api.put("/rate-cards", {
        brand, service_type_id: st.id,
        property_id: propertyId === "general" ? null : propertyId,   // Fix G
        precio_cliente_cents: toCents(precio), costo_sub_cents: toCents(costo), unidad,
      })).data,
    onSuccess: () => {
      toast.success(t("rates.saved"));
      setDirty(false);
      onSaved();
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const margen = toCents(precio) - toCents(costo);

  return (
    <tr data-testid={`rate-row-${brand}-${st.id}`} className="h-[44px] border-b border-line last:border-0">
      <td className="px-3 font-medium">
        {st.nombre}
        {/* Fix G: si estás mirando un edificio y esta fila todavía sale de la lista general,
            hay que decirlo: si no, se edita creyendo que se cambia la del edificio. */}
        {propertyId !== "general" && rate?.origen === "general" ? (
          <span className="ml-2 text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
            {t("rates.from_general")}
          </span>
        ) : null}
      </td>
      <td className="px-3">
        <Input
          data-testid={`rate-price-input-${brand}-${st.id}`}
          value={precio} inputMode="decimal" placeholder="0.00" disabled={!canEdit}
          onChange={(e) => { setPrecio(e.target.value); setDirty(true); }}
          className="h-8 w-28 rounded-[6px] text-right text-[13px] tabular-nums"
        />
      </td>
      <td className="px-3">
        <Input
          data-testid={`rate-cost-input-${brand}-${st.id}`}
          value={costo} inputMode="decimal" placeholder="0.00" disabled={!canEdit}
          onChange={(e) => { setCosto(e.target.value); setDirty(true); }}
          className="h-8 w-28 rounded-[6px] text-right text-[13px] tabular-nums"
        />
      </td>
      <td className="px-3">
        <Select value={unidad} onValueChange={(v) => { setUnidad(v); setDirty(true); }} disabled={!canEdit}>
          <SelectTrigger data-testid={`rate-unit-select-${brand}-${st.id}`} className="h-8 w-36 rounded-[6px] text-[12.5px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNITS.map((u) => (
              <SelectItem key={u} value={u}>{t(`rates.units.${u}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 text-right tabular-nums text-ink-tertiary">{formatMoney(margen)}</td>
      <td className="px-3 text-right">
        {canEdit && (
          <Button
            size="sm" variant="outline"
            data-testid={`rate-save-button-${brand}-${st.id}`}
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            className="h-7 rounded-[6px] text-[12px]"
          >
            {t("rates.save")}
          </Button>
        )}
      </td>
    </tr>
  );
};

export const RatesTab = () => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const canEdit = can("service_types", "update");
  const [brand, setBrand] = useState("bb");
  const [propertyId, setPropertyId] = useState("general");   // Fix G: «general» o el id de un edificio
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const { data: serviceTypes } = useQuery({
    queryKey: ["service-types"],
    queryFn: async () => (await api.get("/service-types", { params: { status: "active" } })).data,
  });
  const { data: properties } = useQuery({
    queryKey: ["properties-for-rates", brand],
    queryFn: async () => (await api.get("/properties", { params: { brand, page_size: 200, status: "active" } })).data,
  });
  const { data: rates, refetch } = useQuery({
    queryKey: ["rate-cards", brand, propertyId],
    queryFn: async () => (await api.get("/rate-cards", {
      params: { brand, property_id: propertyId === "general" ? undefined : propertyId },
    })).data,
  });
  const { data: exceptions } = useQuery({
    queryKey: ["rate-cards-exceptions"],
    queryFn: async () => (await api.get("/rate-cards/excepciones")).data,
  });

  // Fix G: con un edificio elegido llegan las dos listas; la del edificio pisa a la general,
  // y las filas sin tarifa propia se muestran igual para poder crearla.
  const byService = useMemo(() => {
    const mapa = {};
    for (const r of rates?.items || []) {
      if (!mapa[r.service_type_id] || r.origen === "edificio") mapa[r.service_type_id] = r;
    }
    return mapa;
  }, [rates]);

  const exportCsv = () => {
    const rows = [["servicio", "marca", "precio_cliente", "costo_sub", "unidad"]];
    for (const st of serviceTypes?.items || []) {
      const r = byService[st.id];
      rows.push([
        st.nombre, brand,
        r ? (r.precio_cliente_cents / 100).toFixed(2) : "",
        r ? (r.costo_sub_cents / 100).toFixed(2) : "",
        r?.unidad || "trabajo",
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tarifario_${brand}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const header = lines[0].split(",").map((h) => h.replace(/"/g, "").trim().toLowerCase());
      const idx = (name) => header.indexOf(name);
      const byName = Object.fromEntries((serviceTypes?.items || []).map((s) => [s.nombre.trim().toLowerCase(), s]));
      let creados = 0, saltados = 0;
      for (const line of lines.slice(1)) {
        const cols = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
        const nombre = cols[idx("servicio")];
        const st = byName[(nombre || "").toLowerCase()];
        if (!st) { saltados += 1; continue; }
        const rowBrand = cols[idx("marca")] || brand;
        await api.put("/rate-cards", {
          brand: rowBrand, service_type_id: st.id,
          property_id: propertyId === "general" ? null : propertyId,   // Fix G: importar respeta la lista elegida
          precio_cliente_cents: toCents(cols[idx("precio_cliente")]),
          costo_sub_cents: toCents(cols[idx("costo_sub")]),
          unidad: cols[idx("unidad")] || "trabajo",
        });
        creados += 1;
      }
      toast.success(t("rates.import_done", { n: creados }));
      if (saltados) toast.info(t("rates.import_skipped", { n: saltados }));
      qc.invalidateQueries({ queryKey: ["rate-cards"] });
      setImportOpen(false);
      setFile(null);
      refetch();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="rates-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-[13px] text-ink-tertiary">{t("rates.subtitle")}</p>
          <Select value={brand} onValueChange={(v) => { setBrand(v); setPropertyId("general"); }}>
            <SelectTrigger data-testid="rates-brand-select" className="h-8 w-32 rounded-[6px] text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BRANDS.map((b) => (
                <SelectItem key={b} value={b}>{b === "bb" ? "B&B" : "Betos"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Fix G (2026-09-03): cuatro edificios tienen lista propia. Sin este selector, esas
              tarifas existen en la base pero nadie las puede ver ni corregir desde la pantalla. */}
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger data-testid="rates-property-select" className="h-8 w-56 rounded-[6px] text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">{t("rates.list_general")}</SelectItem>
              {(properties?.items || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="rates-export-button" onClick={exportCsv} className="h-8 rounded-[6px] text-[12.5px]">
            <Download className="mr-1.5 h-3.5 w-3.5" /> {t("rates.export")}
          </Button>
          {canEdit && (
            <Button variant="outline" size="sm" data-testid="rates-import-button" onClick={() => setImportOpen(true)} className="h-8 rounded-[6px] text-[12.5px]">
              <Upload className="mr-1.5 h-3.5 w-3.5" /> {t("rates.import")}
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-[8px] border border-line bg-white">
        <table className="w-full text-[13px]" data-testid="rates-table">
          <thead className="bg-surface-sunken">
            <tr className="border-b border-line text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">
              <th className="px-3 py-2 text-left">{t("rates.service")}</th>
              <th className="px-3 py-2 text-left">{t("rates.client_price")}</th>
              <th className="px-3 py-2 text-left">{t("rates.sub_cost")}</th>
              <th className="px-3 py-2 text-left">{t("rates.unit")}</th>
              <th className="px-3 py-2 text-right">{t("rates.margin")}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(serviceTypes?.items || []).map((st) => (
              <RateRow key={`${brand}-${propertyId}-${st.id}-${byService[st.id]?.id ?? "none"}`} brand={brand} propertyId={propertyId} st={st} rate={byService[st.id]} canEdit={canEdit} onSaved={refetch} />
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <p className="mb-1 text-[13px] font-semibold text-ink">{t("rates.exceptions_title")}</p>
        <p className="mb-3 text-[12.5px] text-ink-tertiary">{t("rates.exceptions_desc")}</p>
        {exceptions?.items?.length ? (
          <div className="overflow-hidden rounded-[8px] border border-line bg-white" data-testid="rates-exceptions-table">
            <table className="w-full text-[13px]">
              <thead className="bg-surface-sunken">
                <tr className="border-b border-line text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">
                  <th className="px-3 py-2 text-left">{t("rates.order")}</th>
                  <th className="px-3 py-2 text-left">{t("rates.service")}</th>
                  <th className="px-3 py-2 text-right">{t("rates.no_rate")}</th>
                  <th className="px-3 py-2 text-right">{t("wo.price")}</th>
                  <th className="px-3 py-2 text-left">{t("rates.motive")}</th>
                  <th className="px-3 py-2 text-left">{t("rates.by")}</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.items.map((e) => (
                  <tr key={e.id} data-testid={`rate-exception-${e.id}`} className="h-[44px] border-b border-line last:border-0">
                    <td className="px-3">
                      <Link to={`/ordenes/${e.id}`} className="font-mono text-brand hover:underline">{e.code}</Link>
                    </td>
                    <td className="px-3 text-ink-tertiary">{e.service_nombre}</td>
                    <td className="px-3 text-right tabular-nums">{formatMoney(e.tarifa_precio_cliente_cents)}</td>
                    <td className="px-3 text-right tabular-nums">{formatMoney(e.client_price_cents)}</td>
                    <td className="px-3 text-ink-tertiary">{e.precio_excepcion_motivo || "—"}</td>
                    <td className="px-3 text-ink-tertiary">{e.created_by_nombre || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState testId="rates-exceptions-empty" title={t("rates.exceptions_empty")} description="" />
        )}
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="rates-import-dialog" className="max-w-[460px] rounded-[8px]">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("rates.import_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-[12.5px] text-ink-tertiary">{t("rates.import_help")}</p>
          <Input
            type="file" accept=".csv"
            data-testid="rates-import-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="h-9 rounded-[6px] text-[13px]"
          />
          <DialogFooter>
            <Button variant="outline" className="h-9 rounded-[6px] text-[13px]" onClick={() => setImportOpen(false)}>
              {t("app.cancel")}
            </Button>
            <Button
              data-testid="rates-import-confirm-button"
              disabled={!file || importing}
              onClick={runImport}
              className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              {t("rates.import")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
